import React, { useEffect, useMemo, useRef, useState } from 'react';
import { problems } from './problemData';
import ScrollReveal from '../premium/ScrollReveal';
import './ProblemSection.css';

type Dir = -1 | 0 | 1;

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const ProblemSection: React.FC = () => {
    const [index, setIndex] = useState(0);
    const trackRef = useRef<HTMLDivElement>(null);

    // --- Mobile detection (simple y robusto) ---
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 820 : false
    );
    useEffect(() => {
        const onR = () => setIsMobile(window.innerWidth <= 820);
        onR();
        window.addEventListener('resize', onR, { passive: true });
        return () => window.removeEventListener('resize', onR);
    }, []);

    const go = (dir: Dir) => {
        setIndex((i) => {
            const next = i + dir;
            if (next < 0) return problems.length - 1;
            if (next > problems.length - 1) return 0;
            return next;
        });
    };

    // drag / swipe
    const startX = useRef(0);
    const lastX = useRef(0);
    const dragging = useRef(false);

    const onDown = (e: React.MouseEvent | React.TouchEvent) => {
        dragging.current = true;
        startX.current = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        lastX.current = startX.current;
    };
    const onMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!dragging.current) return;
        lastX.current = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    };
    const onUp = () => {
        if (!dragging.current) return;
        const delta = lastX.current - startX.current;
        dragging.current = false;
        if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
    };

    // keyboard
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') go(1);
            if (e.key === 'ArrowLeft') go(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // autoplay suave
    useEffect(() => {
        const id = setInterval(() => go(1), 6500);
        return () => clearInterval(id);
    }, []);

    const dots = useMemo(() => new Array(problems.length).fill(0), []);

    // --- Configs por breakpoint ---
    const cfg = isMobile
        ? {
            clampN: 1.5,      // mostramos justo vecinos
            spacingVW: 64,    // MÁS separación lateral en mobile
            tilt: -6,         // un poco menos de tilt
            scaleDrop: 0.22,  // reducimos más la escala de los lados
            blurK: 0.9,       // desenfoque moderado
            zStep: 70,        // profundidad para la central
        }
        : {
            clampN: 2,
            spacingVW: 28,
            tilt: -10,
            scaleDrop: 0.12,
            blurK: 1.2,
            zStep: 60,
        };

    return (
        <section className="problem-carousel full-bleed" onMouseLeave={onUp}>
            <div className="container container--edge">
                <ScrollReveal className="pc-header pc-header--showcase">
                    <span className="pc-eyebrow">Why trials stall</span>
                    <h2 className="pc-title">
                        The <span className="pc-title-accent">problems</span> today
                    </h2>
                    <p className="pc-subtitle">
                        Setup drag, fragmented compliance, slow recruitment and disconnected data create
                        months of lost time and cost.
                    </p>
                </ScrollReveal>

                <div
                    className="pc-stage"
                    ref={trackRef}
                    onMouseDown={onDown}
                    onMouseMove={onMove}
                    onMouseUp={onUp}
                    onTouchStart={onDown}
                    onTouchMove={onMove}
                    onTouchEnd={onUp}
                >
                    {problems.map((p, i) => {
                        const offset = i - index;

                        // envolvemos para elegir la ruta más corta
                        const shortest =
                            Math.abs(offset) <= problems.length / 2
                                ? offset
                                : offset > 0
                                    ? offset - problems.length
                                    : offset + problems.length;

                        // clamp distinto en mobile
                        const t = clamp(shortest, -cfg.clampN, cfg.clampN);

                        // mapeos a transform
                        const translateX = t * cfg.spacingVW;          // separa más en mobile
                        const rotateY = t * cfg.tilt;
                        const scale = 1 - Math.abs(t) * cfg.scaleDrop;
                        const blur = Math.abs(t) * cfg.blurK;
                        const zIndex = 100 - Math.abs(t) * 10;
                        const zDepth = (cfg.clampN - Math.abs(t)) * cfg.zStep; // empuja la central hacia delante

                        return (
                            <article
                                key={p.id}
                                className={`pc-card ${t === 0 ? 'is-center' : ''}`}
                                style={{
                                    transform: `translateX(${translateX}vw) translateZ(${zDepth}px) scale(${scale}) rotateY(${rotateY}deg)`,
                                    zIndex,
                                    filter: `blur(${blur}px)`,
                                }}
                            >
                                <div className="pc-card-surface">
                                    <div className="pc-media">
                                        <img src={p.image} alt={p.title} />
                                    </div>
                                    <div className="pc-body">
                                        <h3 className="pc-card-title">{p.title}</h3>
                                        <p className="pc-card-desc">{p.description}</p>
                                        {p.badges?.length ? (
                                            <div className="pc-badges">
                                                {p.badges.map((b) => (
                                                    <span className="pc-badge" key={b}>
                            {b}
                          </span>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <div className="pc-controls">
                    <button className="pc-arrow" onClick={() => go(-1)} aria-label="Previous">
                        ←
                    </button>
                    <div className="pc-dots" role="tablist" aria-label="Problems">
                        {dots.map((_, i) => (
                            <button
                                key={i}
                                className={`pc-dot ${i === index ? 'is-active' : ''}`}
                                onClick={() => setIndex(i)}
                                aria-label={`Go to slide ${i + 1}`}
                                aria-selected={i === index}
                                role="tab"
                            />
                        ))}
                    </div>
                    <button className="pc-arrow" onClick={() => go(1)} aria-label="Next">
                        →
                    </button>
                </div>
            </div>
        </section>
    );
};

export default ProblemSection;
